import {
  CloseButton,
  PopupContainer,
  PopupContent,
  TitleShadow,
  TitleShadowBullas,
  TitleShadowHenlo,
  TitleShadowPuffpaw,
} from "@/components/styles";
import Image from "@/components/ui/image";
import useUserStore from "@/state/user/useUserStore";
import { m } from "motion/react";
import { Download, ExternalLink, X } from "lucide-react";
import React from "react";
import NextImage from "next/image";
import { domToPng } from "modern-screenshot";
import { usePathname } from "next/navigation";
import { UploadGallery } from "@/app/actions/upload-gallery";
import { toast } from "sonner";
import { trackEvent } from "@openpanel/nextjs";

const Popup = ({
  popupRef,
  imageSrc,
  color,
  memeRef,
  twitter,
  setIsPopupOpen,
  customFont,
  isLoading,
  tweetText,
  websiteLink,
  replyTo,
  noShare,
}: {
  popupRef: React.RefObject<HTMLDivElement>;
  imageSrc: string | null;
  color: string;
  memeRef: React.RefObject<HTMLDivElement>;
  twitter: string;
  setIsPopupOpen: (isPopupOpen: boolean) => void;
  customFont?: string;
  isLoading: boolean;
  tweetText?: string;
  websiteLink?: string;
  replyTo?: string;
  noShare?: boolean;
}) => {
  const [ratio, hasUploadedGallery, setHasUploadedGallery] = useUserStore(
    (state) => [
      state.ratio,
      state.hasUploadedGallery,
      state.setHasUploadedGallery,
    ]
  );

  const convertToPng = async () => {
    if (memeRef.current !== null) {
      try {
        const dataUrl = await domToPng(memeRef.current, {
          scale: 2,
          backgroundColor: null,
        });

        const link = document.createElement("a");
        link.download = "beraflip-meme.png";
        link.href = dataUrl;
        link.click();

        if (!hasUploadedGallery) {
          const data = await UploadGallery({ dataUrl });
          if (data) {
            setHasUploadedGallery(true);
            toast("Your meme has been uploaded to the gallery!");
          }
        }
      } catch (error) {
        console.error("Error capturing image:", error);
      }
    }
  };

  const text = encodeURIComponent(tweetText ?? "");

  const pathname = usePathname();
  const partnerName = pathname
    ? pathname.slice(1).split("/")[0].charAt(0).toUpperCase() +
      pathname.slice(1).split("/")[0].slice(1) +
      (pathname.slice(1).split("/")[0].endsWith("s") ? "'" : "'s")
    : "The Honey Jar's";

  return (
    <PopupContainer>
      <PopupContent ref={popupRef}>
        <div
          style={{
            position: "absolute",
            width: "300px",
            height: "300px",
            bottom: "-75px",
            left: "-75px",
            zIndex: 0,
            transform: "rotate(24deg)",
          }}
        >
          <Image
            src="/Home/honey_jar_globe_black.png"
            alt=""
            fill
            style={{
              objectFit: "contain",
              opacity: 0.05,
            }}
          />
        </div>
        <div
          style={{
            position: "absolute",
            top: "24px",
            right: "24px",
            cursor: "pointer",
            zIndex: 999,
          }}
          onClick={() => setIsPopupOpen(false)}
        >
          <CloseButton color={color}>
            <X />
          </CloseButton>
        </div>
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            zIndex: 99,
          }}
        >
          {customFont === "henlo" && (
            <TitleShadowHenlo style={{ margin: 0, marginBottom: "4px" }}>
              Download and share
            </TitleShadowHenlo>
          )}
          {customFont === "bullas" && (
            <TitleShadowBullas style={{ margin: 0, marginBottom: "4px" }}>
              Download and share
            </TitleShadowBullas>
          )}
          {customFont === "puffpaw" && (
            <TitleShadowPuffpaw style={{ margin: 0, marginBottom: "4px" }}>
              Download and share
            </TitleShadowPuffpaw>
          )}
          {!customFont && (
            <TitleShadow style={{ margin: 0, marginBottom: "4px" }}>
              Download and share
            </TitleShadow>
          )}
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "16px",
            width: "100%",
            zIndex: 99,
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              width: "60%",
            }}
          >
            <div
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                background: "#aeaeae",
                boxSizing: "border-box",
                padding: "16px",
                borderRadius: "0.25rem",
                border: "1px solid #00000050",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "16px",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "32px",
                    height: "32px",
                    borderRadius: "0.25rem",
                    background: "#000000",
                    color: "#fff",
                  }}
                >
                  1
                </div>
                <p style={{ margin: 0, padding: 0 }}>Download your meme</p>
              </div>
              <m.div
                whileHover={{
                  scale: 1.02,
                  transition: {
                    duration: 0.15,
                    ease: "linear",
                  },
                }}
                whileTap={{ scale: 0.99 }}
                style={{ width: "8rem", height: "100%" }}
              >
                <button
                  style={{
                    width: "100%",
                    height: "100%",
                    padding: "8px",
                    borderRadius: "0.25rem",
                    boxSizing: "border-box",
                    background: color,
                    color: "#000",
                    cursor: "pointer",
                    fontFamily: "var(--font-cabin-condensed)",
                    fontSize: "1rem",
                    boxShadow: "2px 2px 0px 0px #000000",
                    border: "4px solid #000000",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                  onClick={() => {
                    trackEvent("download_meme");
                    convertToPng();
                  }}
                >
                  <Download
                    style={{
                      position: "relative",
                      width: "16px",
                      height: "16px",
                    }}
                  />
                  <p
                    style={{
                      padding: 0,
                      margin: 0,
                      fontSize: "1rem",
                    }}
                  >
                    Download
                  </p>
                </button>
              </m.div>
            </div>
            {!noShare && (
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  background: "#aeaeae",
                  boxSizing: "border-box",
                  padding: "16px",
                  borderRadius: "0.25rem",
                  border: "1px solid #00000050",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "16px",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "32px",
                      height: "32px",
                      borderRadius: "0.25rem",
                      background: "#000000",
                      color: "#fff",
                    }}
                  >
                    2
                  </div>
                  {replyTo ? (
                    <p style={{ margin: 0, padding: 0 }}>
                      Share your meme on X and reply to{" "}
                      <a
                        style={{
                          color: "#2563eb",
                          textDecoration: "underline",
                          cursor: "pointer",
                        }}
                        href={`https://twitter.com/intent/tweet?&in_reply_to=${replyTo}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        tweet
                      </a>
                    </p>
                  ) : (
                    <p style={{ margin: 0, padding: 0 }}>
                      Share your meme on X and tag{" "}
                      <a
                        style={{
                          color: "#2563eb",
                          textDecoration: "underline",
                          cursor: "pointer",
                        }}
                        href={`https://twitter.com/${twitter}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        @{twitter}
                      </a>
                    </p>
                  )}
                </div>
                <m.div
                  whileHover={{
                    scale: 1.02,
                    transition: {
                      duration: 0.15,
                      ease: "linear",
                    },
                  }}
                  whileTap={{ scale: 0.99 }}
                  style={{ width: "8rem", height: "100%" }}
                >
                  <a
                    className="twitter-share-button"
                    href={
                      replyTo
                        ? `https://twitter.com/intent/tweet?&in_reply_to=${replyTo}`
                        : `https://twitter.com/intent/tweet?text=%20@${twitter} ${text}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none" }}
                    onClick={() => {
                      trackEvent("share_meme");
                    }}
                  >
                    <button
                      style={{
                        width: "100%",
                        height: "100%",
                        padding: "8px",
                        borderRadius: "0.25rem",
                        boxSizing: "border-box",
                        background: "#000",
                        color: "#fff",
                        cursor: "pointer",
                        fontFamily: "var(--font-cabin-condensed)",
                        fontSize: "1rem",
                        boxShadow: "2px 2px 0px 0px #000000",
                        border: "4px solid #000000",
                        position: "relative",
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                      }}
                    >
                      <div
                        style={{
                          position: "relative",
                          width: "16px",
                          height: "16px",
                        }}
                      >
                        <Image src="/Home/logo-white.png" alt="" fill />
                      </div>
                      <p
                        style={{
                          padding: 0,
                          margin: 0,
                          fontSize: "1rem",
                        }}
                      >
                        Share
                      </p>
                    </button>
                  </a>
                </m.div>
              </div>
            )}
            {websiteLink && (
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  background: "#aeaeae",
                  boxSizing: "border-box",
                  padding: "16px",
                  borderRadius: "0.25rem",
                  border: "1px solid #00000050",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "16px",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "32px",
                      height: "32px",
                      borderRadius: "0.25rem",
                      background: "#000000",
                      color: "#fff",
                    }}
                  >
                    3
                  </div>
                  <p style={{ margin: 0, padding: 0 }}>
                    Checkout{" "}
                    {pathname && pathname.length > 1 && pathname !== "/gallery"
                      ? partnerName
                      : "The Honey Jar's"}{" "}
                    website
                  </p>
                </div>
                <m.div
                  whileHover={{
                    scale: 1.02,
                    transition: {
                      duration: 0.15,
                      ease: "linear",
                    },
                  }}
                  whileTap={{ scale: 0.99 }}
                  style={{ width: "8rem", height: "100%" }}
                >
                  <a
                    style={{
                      width: "100%",
                      height: "100%",
                      padding: "8px",
                      borderRadius: "0.25rem",
                      boxSizing: "border-box",
                      background: color,
                      color: "#000",
                      cursor: "pointer",
                      fontFamily: "var(--font-cabin-condensed)",
                      fontSize: "1rem",
                      boxShadow: "2px 2px 0px 0px #000000",
                      border: "4px solid #000000",
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      textDecoration: "none",
                    }}
                    href={websiteLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      trackEvent("visit_website");
                    }}
                  >
                    <ExternalLink
                      style={{
                        position: "relative",
                        width: "16px",
                        height: "16px",
                      }}
                    />
                    <p
                      style={{
                        padding: 0,
                        margin: 0,
                        fontSize: "1rem",
                      }}
                    >
                      Website
                    </p>
                  </a>
                </m.div>
              </div>
            )}
          </div>
          <div
            style={{
              width: "40%",
              aspectRatio: ratio,
              position: "relative",
            }}
          >
            <div
              style={{
                maxWidth: "100%",
                width: "100%",
                height: "auto",
                aspectRatio: ratio,
                background: "#00000050",
                borderRadius: "0.25rem",
                overflow: "hidden",
                border: "1px solid #00000050",
                position: "relative",
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    width: "100px",
                    height: "100px",
                    position: "relative",
                  }}
                >
                  <Image
                    src="/Home/honey_jar_globe_black.png"
                    alt=""
                    width={100}
                    fill
                    style={{
                      objectFit: "contain",
                      opacity: 0.05,
                    }}
                    sizes="100px"
                  />
                </div>
              </div>
              {imageSrc && !isLoading && (
                <NextImage
                  src={imageSrc}
                  alt="Captured Content"
                  fill
                  style={{ objectFit: "contain" }}
                  onContextMenu={(e) => e.preventDefault()}
                />
              )}
            </div>
          </div>
        </div>
      </PopupContent>
    </PopupContainer>
  );
};

export default Popup;
